import {useCallback, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {
  Archive,
  CreditCard,
  Package,
  RotateCcw,
  ShoppingCart,
  Star,
  TrendingUp,
  Users,
  Wallet
} from "lucide-react";
import {
  getAdminDashboard,
  getAdminSession,
  getAdminSymphonyaDashboard,
  type AdminDashboardPeriod,
  type AdminDashboardStats,
  type AdminSymphonyaDashboard,
  type AdminUser
} from "@zaaaz/shared";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {AdminAlert, AdminCard, AdminLoading, AdminPageLayout, AdminStatCard} from "./ui";
import {SymphonyaSummaryCard} from "./SymphonyaSummaryCard";

const PERIODS: {id: AdminDashboardPeriod; label: string}[] = [
  {id: "today", label: "Today"},
  {id: "thisMonth", label: "This Month"},
  {id: "lastMonth", label: "Last Month"},
  {id: "last30", label: "Last 30 days"}
];

const QUICK_LINKS = [
  {href: "/vadmin/comenzi", label: "Orders", description: "Manage active orders", icon: ShoppingCart, tone: "primary" as const},
  {href: "/vadmin/produse", label: "Products", description: "Catalog, offers, and reviews", icon: Archive, tone: "neutral" as const},
  {href: "/vadmin/clienti", label: "Clients", description: "Accounts and B2B groups", icon: Users, tone: "neutral" as const}
];

function rangeHint(stats: AdminDashboardStats): string {
  if(stats.dateFrom && stats.dateTo) {
    return stats.dateFrom === stats.dateTo ? stats.dateFrom : `${stats.dateFrom} – ${stats.dateTo}`;
  }
  return stats.periodLabel || stats.date;
}

export function DashboardView() {
  const [period, setPeriod] = useState<AdminDashboardPeriod>("thisMonth");
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [symphonya, setSymphonya] = useState<AdminSymphonyaDashboard | null>(null);
  const [err, setErr] = useState("");
  const [loadingStats, setLoadingStats] = useState(false);

  const loadStats = useCallback((p: AdminDashboardPeriod) => {
    setLoadingStats(true);
    getAdminDashboard({period: p})
      .then((s) => { setStats(s); setErr(""); })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoadingStats(false));
  }, []);

  useEffect(() => {
    loadStats(period);
  }, [period, loadStats]);

  useEffect(() => {
    getAdminSession().then(setUser).catch(() => {});
  }, []);

  useEffect(() => {
    if(user?.rol === 0 || user?.rol === 1) {
      getAdminSymphonyaDashboard().then(setSymphonya).catch(() => setSymphonya(null));
    }
  }, [user?.rol]);

  if(err && !stats) return <AdminAlert variant="error">{err}</AdminAlert>;
  if(!stats) return <AdminLoading lines={6} />;

  const hint = rangeHint(stats);
  const periodLabel = stats.periodLabel || PERIODS.find((p) => p.id === period)?.label || "This month";

  return (
    <AdminPageLayout
      title={user?.nume ? `Welcome back, ${user.nume.split(" ")[0]}` : "Dashboard"}
      description={`${periodLabel} store summary${hint ? ` · ${hint}` : ""}.`}
      breadcrumbs={[{label: "Dashboard"}]}
      actions={
        <div className="admin-period-switch" role="tablist" aria-label="Dashboard period">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={period === p.id}
              className={cn("admin-period-switch__btn", period === p.id && "isActive")}
              disabled={loadingStats && period === p.id}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      }
    >
      {err ? <AdminAlert variant="error">{err}</AdminAlert> : null}

      <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4", loadingStats && "opacity-70")}>
        <AdminStatCard label="Sales" value={`${stats.salesTotalLei} lei`} hint={`Non-canceled · ${hint}`} icon={TrendingUp} tone="accent" />
        <AdminStatCard label="Orders" value={stats.ordersTotal} hint={`All payment methods · ${hint}`} icon={Package} />
        <AdminStatCard label="Orders card" value={stats.ordersCard} hint={`Card (Stripe) · ${hint}`} icon={CreditCard} />
        <AdminStatCard label="COD orders" value={stats.ordersCod} hint={`Cash on delivery · ${hint}`} icon={Wallet} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {QUICK_LINKS.map((link) => {
          const Icon = link.icon;
          return (
            <Link key={link.href} to={link.href} className="group block">
              <AdminCard className="h-full transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md">
                <div className="flex flex-row items-start gap-4">
                  <div className={link.tone === "primary" ? "admin-dash-icon admin-dash-icon--accent" : "admin-dash-icon"}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="admin-card__title text-base">{link.label}</h2>
                    <p className="admin-card__description">{link.description}</p>
                    <span className="mt-3 inline-block text-sm font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">Open section →</span>
                  </div>
                </div>
              </AdminCard>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <AdminStatCard label="Registered clients" value={stats.clientsTotal} icon={Users} />
        <AdminCard>
          <div className="flex items-center gap-3 pb-4">
            <div className="admin-dash-icon"><RotateCcw className="h-5 w-5" /></div>
            <div>
              <h2 className="admin-card__title text-base">Returns</h2>
              <p className="admin-card__description">Pending requests</p>
            </div>
          </div>
          <div className="flex items-end justify-between gap-3">
            <p className="text-3xl font-semibold tracking-tight">{stats.counts.returns}</p>
            <Button variant="outline" size="sm" asChild>
              <Link to="/vadmin/comenzi/returnare">View returns</Link>
            </Button>
          </div>
        </AdminCard>
        <AdminCard>
          <div className="flex items-center gap-3 pb-4">
            <div className="admin-dash-icon"><Star className="h-5 w-5" /></div>
            <div>
              <h2 className="admin-card__title text-base">Vouchers</h2>
              <p className="admin-card__description">Active codes in the system</p>
            </div>
          </div>
          <div className="flex items-end justify-between gap-3">
            <p className="text-3xl font-semibold tracking-tight">{stats.counts.vouchers}</p>
            <Button variant="outline" size="sm" disabled asChild>
              <Link to="/vadmin/comenzi/vouchere">Manage</Link>
            </Button>
          </div>
        </AdminCard>
      </div>

      {(user?.rol === 0 || user?.rol === 1) ? (
        <SymphonyaSummaryCard data={symphonya} />
      ) : null}
    </AdminPageLayout>
  );
}
